// src/schemas/index.ts
import { z } from "zod";
var paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20)
});
var idParamSchema = z.object({
  id: z.string().uuid()
});
var dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)");
var timeSchema = z.string().regex(/^\d{2}:\d{2}$/, "Invalid time format (HH:MM)");
var phoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number").optional().nullable();
var emailSchema = z.string().email("Invalid email address");
var createUserSchema = z.object({
  email: emailSchema,
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  phone: phoneSchema,
  password: z.string().min(8).max(128)
});
var updateUserSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  phone: phoneSchema,
  handicap: z.number().min(-10).max(54).optional().nullable(),
  dateOfBirth: dateSchema.optional().nullable(),
  preferredCourseId: z.string().uuid().optional().nullable(),
  emailNotifications: z.boolean().optional(),
  smsNotifications: z.boolean().optional(),
  marketingOptIn: z.boolean().optional()
});
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1)
});
var resetPasswordSchema = z.object({
  email: emailSchema
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8).max(128)
});
var teeTimeFiltersSchema = z.object({
  courseId: z.string().uuid().optional(),
  date: dateSchema.optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  timeFrom: timeSchema.optional(),
  timeTo: timeSchema.optional(),
  holes: z.enum(["9", "18"]).transform(Number).optional(),
  minPlayers: z.coerce.number().int().min(1).max(4).optional(),
  maxPrice: z.coerce.number().positive().optional(),
  includeBlocked: z.enum(["true", "false"]).transform((v) => v === "true").optional()
});
var createTeeTimeSchema = z.object({
  courseId: z.string().uuid(),
  date: dateSchema,
  time: timeSchema,
  holes: z.union([z.literal(9), z.literal(18)]),
  maxPlayers: z.number().int().min(1).max(4).default(4),
  basePrice: z.number().positive(),
  cartPrice: z.number().nonnegative(),
  isPeakTime: z.boolean().default(false)
});
var updateTeeTimeSchema = z.object({
  maxPlayers: z.number().int().min(1).max(4).optional(),
  basePrice: z.number().positive().optional(),
  cartPrice: z.number().nonnegative().optional(),
  isPeakTime: z.boolean().optional(),
  isBlocked: z.boolean().optional(),
  blockReason: z.string().max(500).optional().nullable()
});
var bulkCreateTeeTimes = z.object({
  courseId: z.string().uuid(),
  dateFrom: dateSchema,
  dateTo: dateSchema,
  startTime: timeSchema,
  endTime: timeSchema,
  intervalMinutes: z.number().int().min(5).max(60).default(10),
  holes: z.union([z.literal(9), z.literal(18)]),
  maxPlayers: z.number().int().min(1).max(4).default(4),
  basePrice: z.number().positive(),
  cartPrice: z.number().nonnegative(),
  peakTimeStart: timeSchema.optional(),
  peakTimeEnd: timeSchema.optional(),
  peakTimeMultiplier: z.number().positive().default(1.5),
  excludeWeekends: z.boolean().default(false),
  excludeDates: z.array(dateSchema).optional()
});
var bookingPlayerSchema = z.object({
  userId: z.string().uuid().optional(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: emailSchema.optional(),
  phone: phoneSchema,
  handicap: z.number().min(-10).max(54).optional().nullable()
});
var createBookingSchema = z.object({
  teeTimeId: z.string().uuid(),
  playerCount: z.number().int().min(1).max(4),
  includesCart: z.boolean().default(false),
  players: z.array(bookingPlayerSchema).min(1).max(4),
  specialRequests: z.string().max(1e3).optional(),
  promoCode: z.string().max(50).optional()
});
var updateBookingSchema = z.object({
  playerCount: z.number().int().min(1).max(4).optional(),
  includesCart: z.boolean().optional(),
  players: z.array(bookingPlayerSchema).min(1).max(4).optional(),
  specialRequests: z.string().max(1e3).optional().nullable(),
  status: z.enum(["pending", "confirmed", "checked_in", "completed", "cancelled", "no_show"]).optional()
});
var cancelBookingSchema = z.object({
  reason: z.string().max(500).optional()
});
var bookingFiltersSchema = z.object({
  userId: z.string().uuid().optional(),
  courseId: z.string().uuid().optional(),
  status: z.union([
    z.enum(["pending", "confirmed", "checked_in", "completed", "cancelled", "no_show"]),
    z.array(z.enum(["pending", "confirmed", "checked_in", "completed", "cancelled", "no_show"]))
  ]).optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  search: z.string().max(100).optional()
});
var productVariantSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(100),
  sku: z.string().min(1).max(100),
  price: z.number().positive().optional().nullable(),
  quantity: z.number().int().nonnegative(),
  options: z.record(z.string()).default({})
});
var createProductSchema = z.object({
  courseId: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().max(5e3).default(""),
  category: z.enum([
    "clubs",
    "balls",
    "apparel",
    "shoes",
    "bags",
    "accessories",
    "gloves",
    "rentals",
    "food_beverage"
  ]),
  price: z.number().positive(),
  compareAtPrice: z.number().positive().optional().nullable(),
  costPrice: z.number().nonnegative().optional().nullable(),
  sku: z.string().min(1).max(100),
  barcode: z.string().max(100).optional().nullable(),
  quantity: z.number().int().nonnegative().default(0),
  lowStockThreshold: z.number().int().nonnegative().default(5),
  imageUrl: z.string().url().optional().nullable(),
  images: z.array(z.string().url()).default([]),
  isActive: z.boolean().default(true),
  isFeatured: z.boolean().default(false),
  isRental: z.boolean().default(false),
  rentalDuration: z.number().int().positive().optional().nullable(),
  variants: z.array(productVariantSchema).default([])
});
var updateProductSchema = createProductSchema.partial().omit({ courseId: true });
var productFiltersSchema = z.object({
  courseId: z.string().uuid().optional(),
  category: z.union([
    z.enum([
      "clubs",
      "balls",
      "apparel",
      "shoes",
      "bags",
      "accessories",
      "gloves",
      "rentals",
      "food_beverage"
    ]),
    z.array(
      z.enum([
        "clubs",
        "balls",
        "apparel",
        "shoes",
        "bags",
        "accessories",
        "gloves",
        "rentals",
        "food_beverage"
      ])
    )
  ]).optional(),
  minPrice: z.coerce.number().nonnegative().optional(),
  maxPrice: z.coerce.number().positive().optional(),
  inStock: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  isFeatured: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  isRental: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  search: z.string().max(100).optional()
});
var orderItemSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid().optional().nullable(),
  quantity: z.number().int().positive()
});
var createOrderSchema = z.object({
  courseId: z.string().uuid(),
  bookingId: z.string().uuid().optional(),
  items: z.array(orderItemSchema).min(1),
  promoCode: z.string().max(50).optional(),
  notes: z.string().max(1e3).optional()
});
var updateOrderSchema = z.object({
  status: z.enum(["pending", "paid", "cancelled", "refunded"]).optional(),
  fulfillmentStatus: z.enum(["unfulfilled", "partially_fulfilled", "fulfilled", "picked_up"]).optional(),
  notes: z.string().max(1e3).optional().nullable()
});
var orderFiltersSchema = z.object({
  userId: z.string().uuid().optional(),
  courseId: z.string().uuid().optional(),
  status: z.union([
    z.enum(["pending", "paid", "cancelled", "refunded"]),
    z.array(z.enum(["pending", "paid", "cancelled", "refunded"]))
  ]).optional(),
  fulfillmentStatus: z.union([
    z.enum(["unfulfilled", "partially_fulfilled", "fulfilled", "picked_up"]),
    z.array(z.enum(["unfulfilled", "partially_fulfilled", "fulfilled", "picked_up"]))
  ]).optional(),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  search: z.string().max(100).optional()
});
var createPaymentSchema = z.object({
  sourceId: z.string().min(1),
  // Square payment token
  bookingId: z.string().uuid().optional(),
  orderId: z.string().uuid().optional(),
  amount: z.number().int().positive(),
  // Amount in cents
  currency: z.string().length(3).default("USD"),
  idempotencyKey: z.string().uuid()
});
var refundPaymentSchema = z.object({
  amount: z.number().int().positive(),
  // Amount in cents
  reason: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid()
});
var createPromoCodeSchema = z.object({
  code: z.string().min(3).max(50).regex(/^[A-Z0-9_-]+$/, "Code must be uppercase alphanumeric with underscores or hyphens"),
  description: z.string().max(500).default(""),
  discountType: z.enum(["percentage", "fixed_amount"]),
  discountValue: z.number().positive(),
  minPurchaseAmount: z.number().nonnegative().optional().nullable(),
  maxDiscountAmount: z.number().positive().optional().nullable(),
  usageLimit: z.number().int().positive().optional().nullable(),
  perUserLimit: z.number().int().positive().default(1),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  applicableTo: z.enum(["all", "bookings", "merchandise"]).default("all"),
  courseIds: z.array(z.string().uuid()).optional().nullable(),
  isActive: z.boolean().default(true)
});
var updatePromoCodeSchema = createPromoCodeSchema.partial();
var validatePromoCodeSchema = z.object({
  code: z.string().min(1).max(50),
  applicableTo: z.enum(["bookings", "merchandise"]),
  courseId: z.string().uuid().optional(),
  amount: z.number().positive()
});
var redeemLoyaltyRewardSchema = z.object({
  rewardId: z.string().uuid(),
  bookingId: z.string().uuid().optional(),
  orderId: z.string().uuid().optional()
});
var adjustLoyaltyPointsSchema = z.object({
  points: z.number().int(),
  description: z.string().min(1).max(500)
});
var createWaitlistEntrySchema = z.object({
  courseId: z.string().uuid(),
  date: dateSchema,
  preferredTimeStart: timeSchema,
  preferredTimeEnd: timeSchema,
  playerCount: z.number().int().min(1).max(4),
  holes: z.union([z.literal(9), z.literal(18)])
});
var createCourseSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(5e3).default(""),
  address: z.string().min(1).max(500),
  city: z.string().min(1).max(100),
  state: z.string().min(2).max(2),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code"),
  phone: z.string().min(10).max(20),
  email: emailSchema,
  website: z.string().url().optional().nullable(),
  logoUrl: z.string().url().optional().nullable(),
  coverImageUrl: z.string().url().optional().nullable(),
  holes: z.number().int().min(9).max(36).default(18),
  par: z.number().int().min(27).max(144).default(72),
  yardage: z.number().int().positive(),
  rating: z.number().min(50).max(80).optional().nullable(),
  slope: z.number().int().min(55).max(155).optional().nullable(),
  timezone: z.string().default("America/New_York"),
  amenities: z.array(
    z.enum([
      "driving_range",
      "putting_green",
      "pro_shop",
      "restaurant",
      "bar",
      "locker_room",
      "cart_rental",
      "club_rental",
      "lessons"
    ])
  ).default([]),
  isActive: z.boolean().default(true)
});
var updateCourseSchema = createCourseSchema.partial();

// src/utils/index.ts
function formatDate(date, options) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options
  });
}
function formatTime(time) {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}
function formatDateTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}
function formatRelativeTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1e3 * 60));
  if (diffDays > 0) {
    return diffDays === 1 ? "Tomorrow" : `In ${diffDays} days`;
  } else if (diffDays < 0) {
    return Math.abs(diffDays) === 1 ? "Yesterday" : `${Math.abs(diffDays)} days ago`;
  } else if (diffHours > 0) {
    return diffHours === 1 ? "In 1 hour" : `In ${diffHours} hours`;
  } else if (diffHours < 0) {
    return Math.abs(diffHours) === 1 ? "1 hour ago" : `${Math.abs(diffHours)} hours ago`;
  } else if (diffMinutes > 0) {
    return `In ${diffMinutes} minutes`;
  } else if (diffMinutes < 0) {
    return `${Math.abs(diffMinutes)} minutes ago`;
  }
  return "Just now";
}
function getDateRange(startDate, endDate, excludeWeekends = false) {
  const dates = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    if (!excludeWeekends || dayOfWeek !== 0 && dayOfWeek !== 6) {
      dates.push(current.toISOString().split("T")[0]);
    }
    current.setDate(current.getDate() + 1);
  }
  return dates;
}
function getTimeSlots(startTime, endTime, intervalMinutes) {
  const slots = [];
  const [startHours, startMins] = startTime.split(":").map(Number);
  const [endHours, endMins] = endTime.split(":").map(Number);
  let currentMinutes = startHours * 60 + startMins;
  const endMinutes = endHours * 60 + endMins;
  while (currentMinutes < endMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const mins = currentMinutes % 60;
    slots.push(`${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
    currentMinutes += intervalMinutes;
  }
  return slots;
}
function isTimeInRange(time, start, end) {
  const [h, m] = time.split(":").map(Number);
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const timeMinutes = h * 60 + m;
  const startMinutes = sh * 60 + sm;
  const endMinutes = eh * 60 + em;
  return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
}
function formatCurrency(amount, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(amount);
}
function formatCents(cents, currency = "USD") {
  return formatCurrency(cents / 100, currency);
}
function calculateTax(amount, taxRate = 0.0825) {
  return Math.round(amount * taxRate * 100) / 100;
}
function calculateDiscount(amount, discountType, discountValue, maxDiscount) {
  let discount = 0;
  if (discountType === "percentage") {
    discount = amount * (discountValue / 100);
  } else {
    discount = discountValue;
  }
  if (maxDiscount !== null && maxDiscount !== void 0) {
    discount = Math.min(discount, maxDiscount);
  }
  return Math.min(discount, amount);
}
function generateConfirmationCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}
function generateOrderNumber() {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `ORD-${timestamp}-${random}`;
}
function generateReferralCode(userId) {
  const prefix = userId.substring(0, 4).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REF-${prefix}${random}`;
}
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + "...";
}
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function titleCase(text) {
  return text.split(" ").map((word) => capitalize(word)).join(" ");
}
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
function isValidPhone(phone) {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/[\s()-]/g, ""));
}
function isValidZipCode(zipCode) {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zipCode);
}
function calculateLoyaltyPoints(amount, tier) {
  const multipliers = {
    bronze: 1,
    silver: 1.25,
    gold: 1.5,
    platinum: 2
  };
  const multiplier = multipliers[tier] || 1;
  return Math.floor(amount * multiplier);
}
function getLoyaltyTierFromPoints(lifetimePoints) {
  if (lifetimePoints >= 5e4) return "platinum";
  if (lifetimePoints >= 2e4) return "gold";
  if (lifetimePoints >= 5e3) return "silver";
  return "bronze";
}
function getLoyaltyTierBenefits(tier) {
  const benefits = {
    bronze: { pointsMultiplier: 1, earlyBookingDays: 0, discountPercent: 0, freeCartRounds: 0 },
    silver: { pointsMultiplier: 1.25, earlyBookingDays: 1, discountPercent: 5, freeCartRounds: 2 },
    gold: { pointsMultiplier: 1.5, earlyBookingDays: 3, discountPercent: 10, freeCartRounds: 4 },
    platinum: { pointsMultiplier: 2, earlyBookingDays: 7, discountPercent: 15, freeCartRounds: 12 }
  };
  return benefits[tier] || benefits.bronze;
}
var AppError = class extends Error {
  constructor(message, code, statusCode = 400, details) {
    super(message);
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.name = "AppError";
  }
  toJSON() {
    return {
      error: this.message,
      code: this.code,
      ...this.details && { details: this.details }
    };
  }
};
function createValidationError(details) {
  return new AppError("Validation failed", "VALIDATION_ERROR", 400, details);
}
function createNotFoundError(resource) {
  return new AppError(`${resource} not found`, "NOT_FOUND", 404);
}
function createUnauthorizedError(message = "Unauthorized") {
  return new AppError(message, "UNAUTHORIZED", 401);
}
function createForbiddenError(message = "Forbidden") {
  return new AppError(message, "FORBIDDEN", 403);
}
function createConflictError(message) {
  return new AppError(message, "CONFLICT", 409);
}
function calculatePagination(params, totalCount) {
  const totalPages = Math.ceil(totalCount / params.pageSize);
  return {
    page: params.page,
    pageSize: params.pageSize,
    totalCount,
    totalPages,
    hasNextPage: params.page < totalPages,
    hasPreviousPage: params.page > 1
  };
}
function calculateOffset(params) {
  return (params.page - 1) * params.pageSize;
}
function groupBy(array, keyFn) {
  return array.reduce(
    (result, item) => {
      const key = keyFn(item);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    },
    {}
  );
}
function unique(array) {
  return [...new Set(array)];
}
function uniqueBy(array, keyFn) {
  const seen = /* @__PURE__ */ new Set();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function sortBy(array, keyFn, desc = false) {
  return [...array].sort((a, b) => {
    const aKey = keyFn(a);
    const bKey = keyFn(b);
    if (aKey < bKey) return desc ? 1 : -1;
    if (aKey > bKey) return desc ? -1 : 1;
    return 0;
  });
}
function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}
function pick(obj, keys) {
  const result = {};
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}
function removeUndefined(obj) {
  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== void 0) {
      result[key] = value;
    }
  });
  return result;
}

// src/constants/index.ts
var BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "checked_in",
  "completed",
  "cancelled",
  "no_show"
];
var BOOKING_STATUS_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  checked_in: "Checked In",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No Show"
};
var BOOKING_STATUS_COLORS = {
  pending: "amber",
  confirmed: "green",
  checked_in: "blue",
  completed: "stone",
  cancelled: "red",
  no_show: "red"
};
var HOLE_OPTIONS = [9, 18];
var MAX_PLAYERS_PER_BOOKING = 4;
var CANCELLATION_POLICY = {
  freeCancellationHours: 24,
  lateCancellationFeePercent: 50,
  noCancellationHours: 2
};
var DEFAULT_TAX_RATE = 0.0825;
var DEPOSIT_PERCENTAGE = 0.25;
var CURRENCY = "USD";
var PRODUCT_CATEGORIES = [
  "clubs",
  "balls",
  "apparel",
  "shoes",
  "bags",
  "accessories",
  "gloves",
  "rentals",
  "food_beverage"
];
var PRODUCT_CATEGORY_LABELS = {
  clubs: "Golf Clubs",
  balls: "Golf Balls",
  apparel: "Apparel",
  shoes: "Shoes",
  bags: "Golf Bags",
  accessories: "Accessories",
  gloves: "Gloves",
  rentals: "Rentals",
  food_beverage: "Food & Beverage"
};
var ORDER_STATUSES = ["pending", "paid", "cancelled", "refunded"];
var FULFILLMENT_STATUSES = [
  "unfulfilled",
  "partially_fulfilled",
  "fulfilled",
  "picked_up"
];
var LOYALTY_TIERS = ["bronze", "silver", "gold", "platinum"];
var LOYALTY_TIER_THRESHOLDS = {
  bronze: 0,
  silver: 5e3,
  gold: 2e4,
  platinum: 5e4
};
var LOYALTY_TIER_LABELS = {
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum"
};
var LOYALTY_TIER_COLORS = {
  bronze: "#CD7F32",
  silver: "#C0C0C0",
  gold: "#FFD700",
  platinum: "#E5E4E2"
};
var POINTS_PER_DOLLAR = 10;
var REFERRAL_BONUS_POINTS = 1e3;
var BIRTHDAY_BONUS_POINTS = 500;
var NOTIFICATION_TYPES = [
  "booking_confirmation",
  "booking_reminder_24h",
  "booking_reminder_2h",
  "booking_cancellation",
  "booking_modification",
  "order_confirmation",
  "order_shipped",
  "weather_alert",
  "marketing",
  "loyalty_points",
  "loyalty_tier_change"
];
var NOTIFICATION_CHANNELS = ["email", "sms", "push"];
var COURSE_AMENITIES = [
  "driving_range",
  "putting_green",
  "pro_shop",
  "restaurant",
  "bar",
  "locker_room",
  "cart_rental",
  "club_rental",
  "lessons"
];
var AMENITY_LABELS = {
  driving_range: "Driving Range",
  putting_green: "Putting Green",
  pro_shop: "Pro Shop",
  restaurant: "Restaurant",
  bar: "Bar",
  locker_room: "Locker Room",
  cart_rental: "Cart Rental",
  club_rental: "Club Rental",
  lessons: "Golf Lessons"
};
var DEFAULT_TEE_TIME_INTERVAL = 10;
var DEFAULT_FIRST_TEE_TIME = "06:00";
var DEFAULT_LAST_TEE_TIME = "18:00";
var US_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu"
];
var US_STATES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia"
};
var ADMIN_ROLES = ["super_admin", "course_admin", "manager", "staff"];
var ADMIN_ROLE_LABELS = {
  super_admin: "Super Admin",
  course_admin: "Course Admin",
  manager: "Manager",
  staff: "Staff"
};
var ADMIN_PERMISSIONS = [
  "bookings:read",
  "bookings:write",
  "bookings:cancel",
  "customers:read",
  "customers:write",
  "inventory:read",
  "inventory:write",
  "reports:read",
  "settings:read",
  "settings:write",
  "promos:read",
  "promos:write",
  "tee_times:read",
  "tee_times:write",
  "loyalty:read",
  "loyalty:write"
];
var ROLE_DEFAULT_PERMISSIONS = {
  super_admin: [...ADMIN_PERMISSIONS],
  course_admin: [...ADMIN_PERMISSIONS],
  manager: [
    "bookings:read",
    "bookings:write",
    "bookings:cancel",
    "customers:read",
    "inventory:read",
    "inventory:write",
    "reports:read",
    "tee_times:read",
    "tee_times:write",
    "promos:read",
    "loyalty:read"
  ],
  staff: [
    "bookings:read",
    "bookings:write",
    "customers:read",
    "inventory:read",
    "tee_times:read"
  ]
};
var API_VERSION = "v1";
var DEFAULT_PAGE_SIZE = 20;
var MAX_PAGE_SIZE = 100;
var RATE_LIMIT = {
  windowMs: 60 * 1e3,
  // 1 minute
  maxRequests: 100,
  authWindowMs: 15 * 60 * 1e3,
  // 15 minutes
  authMaxRequests: 5
};
var VALIDATION = {
  password: {
    minLength: 8,
    maxLength: 128
  },
  name: {
    minLength: 1,
    maxLength: 100
  },
  description: {
    maxLength: 5e3
  },
  specialRequests: {
    maxLength: 1e3
  },
  handicap: {
    min: -10,
    max: 54
  },
  promoCode: {
    minLength: 3,
    maxLength: 50
  }
};
export {
  ADMIN_PERMISSIONS,
  ADMIN_ROLES,
  ADMIN_ROLE_LABELS,
  AMENITY_LABELS,
  API_VERSION,
  AppError,
  BIRTHDAY_BONUS_POINTS,
  BOOKING_STATUSES,
  BOOKING_STATUS_COLORS,
  BOOKING_STATUS_LABELS,
  CANCELLATION_POLICY,
  COURSE_AMENITIES,
  CURRENCY,
  DEFAULT_FIRST_TEE_TIME,
  DEFAULT_LAST_TEE_TIME,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TAX_RATE,
  DEFAULT_TEE_TIME_INTERVAL,
  DEPOSIT_PERCENTAGE,
  FULFILLMENT_STATUSES,
  HOLE_OPTIONS,
  LOYALTY_TIERS,
  LOYALTY_TIER_COLORS,
  LOYALTY_TIER_LABELS,
  LOYALTY_TIER_THRESHOLDS,
  MAX_PAGE_SIZE,
  MAX_PLAYERS_PER_BOOKING,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES,
  ORDER_STATUSES,
  POINTS_PER_DOLLAR,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
  RATE_LIMIT,
  REFERRAL_BONUS_POINTS,
  ROLE_DEFAULT_PERMISSIONS,
  US_STATES,
  US_TIMEZONES,
  VALIDATION,
  adjustLoyaltyPointsSchema,
  bookingFiltersSchema,
  bookingPlayerSchema,
  bulkCreateTeeTimes,
  calculateDiscount,
  calculateLoyaltyPoints,
  calculateOffset,
  calculatePagination,
  calculateTax,
  cancelBookingSchema,
  capitalize,
  changePasswordSchema,
  createBookingSchema,
  createConflictError,
  createCourseSchema,
  createForbiddenError,
  createNotFoundError,
  createOrderSchema,
  createPaymentSchema,
  createProductSchema,
  createPromoCodeSchema,
  createTeeTimeSchema,
  createUnauthorizedError,
  createUserSchema,
  createValidationError,
  createWaitlistEntrySchema,
  dateSchema,
  emailSchema,
  formatCents,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatTime,
  generateConfirmationCode,
  generateOrderNumber,
  generateReferralCode,
  getDateRange,
  getLoyaltyTierBenefits,
  getLoyaltyTierFromPoints,
  getTimeSlots,
  groupBy,
  idParamSchema,
  isTimeInRange,
  isValidEmail,
  isValidPhone,
  isValidZipCode,
  loginSchema,
  omit,
  orderFiltersSchema,
  orderItemSchema,
  paginationSchema,
  phoneSchema,
  pick,
  productFiltersSchema,
  productVariantSchema,
  redeemLoyaltyRewardSchema,
  refundPaymentSchema,
  removeUndefined,
  resetPasswordSchema,
  slugify,
  sortBy,
  teeTimeFiltersSchema,
  timeSchema,
  titleCase,
  truncate,
  unique,
  uniqueBy,
  updateBookingSchema,
  updateCourseSchema,
  updateOrderSchema,
  updateProductSchema,
  updatePromoCodeSchema,
  updateTeeTimeSchema,
  updateUserSchema,
  validatePromoCodeSchema
};
//# sourceMappingURL=index.js.map